import { Button, Card, Form, Input, Radio, Select, Space, Typography, message as antdMessage } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Grid as ZhGrid, Upload as ZhUpload, type UploadRequestOptions } from "zhihao-ui-react";

import request from "@/api";
import WangEditor from "@/views/components/WangEditor";
import type { AdPageManagementRow } from "./AdPageManagementPage";

export interface AdPageFormValues {
    title: string;
    classify: number;
    type: number;
    pictureId?: number;
    content?: string;
    isNeedButton: number;
    buttonType?: number;
    buttonValue?: string;
    buttonName?: string;
}

export interface AdPageFormPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<AdPageManagementRow>;
    loadProductOptions?: (keyword: string) => Promise<AdPageSelectorOption[]>;
    loadProductTypeOptions?: (keyword: string) => Promise<AdPageSelectorOption[]>;
    loadRouteOptions?: (keyword: string) => Promise<AdPageSelectorOption[]>;
    loadInformationOptions?: (keyword: string) => Promise<AdPageSelectorOption[]>;
    create?: (values: AdPageFormValues) => Promise<unknown>;
    update?: (id: number, values: AdPageFormValues) => Promise<unknown>;
}

const EDITOR_FILE_HEADER = /^==ids=(.*?)\n/;

/** 从 Vue 富文本头部读取编辑前已关联的媒体文件 ID。 */
export function getAdPageStoredFileIds(content?: string): number[] {
    const match = content?.match(EDITOR_FILE_HEADER);
    return (
        match?.[1]
            ?.split("--")
            .map(Number)
            .filter(value => Number.isFinite(value)) || []
    );
}

/** 从 Vue 富文本媒体地址的 #id 后缀提取本次仍在使用的文件 ID。 */
export function getAdPageActiveFileIds(content?: string): number[] {
    const activeFileIds: number[] = [];
    const mediaPattern = /<(?:img|video|source)\s+[^>]*src=["'][^"']*#(\d+)["']/gi;
    let match: RegExpExecArray | null;
    while ((match = mediaPattern.exec(content || "")) !== null) {
        const id = Number(match[1]);
        if (id && !activeFileIds.includes(id)) activeFileIds.push(id);
    }
    return activeFileIds;
}

/** 复用 Vue editorManage.setContent 的媒体文件头格式。 */
export function setAdPageEditorContent(content: string, activeFileIds: number[]): string {
    return activeFileIds.length ? `==ids=${activeFileIds.join("--")}\n${content}` : content;
}

export interface AdPageSelectorOption {
    id: number | string;
    label: string;
}

/** 复用 Vue 广告详情接口作为编辑回显来源。 */
export async function loadAdPageFormDetail(id: number): Promise<AdPageManagementRow> {
    const response = await request.service.get<AdPageManagementRow>(`/dataService/admin/advert/find/${id}`);
    return response.data;
}

/** 复用 Vue 新增广告接口。 */
export async function createAdPage(values: AdPageFormValues): Promise<unknown> {
    const response = await request.service.post("/dataService/admin/advert", values);
    return response.data;
}

/** 复用 Vue 编辑广告接口。 */
export async function updateAdPage(id: number, values: AdPageFormValues): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/advert/${id}`, values);
    return response.data;
}

interface AdPageProductSelectorItem {
    id: number | string;
    title?: string;
    productTypeName?: string;
    productTypeSpec?: string | number;
}

interface AdPageProductTypeSelectorItem {
    id: number | string;
    name?: string;
    area?: string;
}

interface AdPageRouteSelectorItem {
    id: number | string;
    source?: string;
    destination?: string;
    sourceFullName?: string;
    destinationFullName?: string;
}

interface AdPageInformationSelectorItem {
    id: number | string;
    title?: string;
}

/** 复用 Vue 商品远程选择接口，并将结果转换为表单选项。 */
export async function loadAdPageProductOptions(keyword: string): Promise<AdPageSelectorOption[]> {
    const response = await request.service.get<{ list?: AdPageProductSelectorItem[] }>(
        "/admin/product-consignment/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.title, item.productTypeName && `${item.productTypeName}(${item.productTypeSpec ?? ""})`, item.id]
            .filter(Boolean)
            .join(" | ")
    }));
}

/** 复用 Vue 砂价远程选择接口，并将结果转换为表单选项。 */
export async function loadAdPageProductTypeOptions(keyword: string): Promise<AdPageSelectorOption[]> {
    const response = await request.service.get<{ list?: AdPageProductTypeSelectorItem[] }>(
        "/dataService/admin/product-type/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.name, item.area].filter(Boolean).join(" | ")
    }));
}

/** 复用 Vue 运价远程选择接口，并将结果转换为表单选项。 */
export async function loadAdPageRouteOptions(keyword: string): Promise<AdPageSelectorOption[]> {
    const response = await request.service.get<{ list?: AdPageRouteSelectorItem[] }>(
        "/dataService/admin/ship/route/paging-selector",
        { params: { shipRouteName: keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [
            item.source && item.destination ? `${item.source}-${item.destination}` : undefined,
            item.sourceFullName && item.destinationFullName
                ? `${item.sourceFullName}-${item.destinationFullName}`
                : undefined
        ]
            .filter(Boolean)
            .join(" | ")
    }));
}

/** 复用 Vue 资讯远程选择接口，并将结果转换为表单选项。 */
export async function loadAdPageInformationOptions(keyword: string): Promise<AdPageSelectorOption[]> {
    const response = await request.service.get<AdPageInformationSelectorItem[]>(
        `/dataService/admin/information/selector?title=${encodeURIComponent(keyword)}`
    );
    return (response.data || []).map(item => ({ id: item.id, label: item.title || String(item.id) }));
}

const initialValues: AdPageFormValues = { title: "", classify: 1, type: 1, isNeedButton: 0 };
const AD_PAGE_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);
const AD_PAGE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const AD_PAGE_IMAGE_DIMENSION_ERROR = "上传的图片分辨率不符合要求";

/** 保持 Vue 广告页按终端分类要求的最低图片分辨率。 */
export function getAdPageImageDimensions(classify: number): [number, number] {
    return classify === 1 ? [1920, 960] : [750, 1428];
}

/** 返回广告图片分辨率校验错误；边界值按 Vue 实现视为合法。 */
export function getAdPageImageDimensionError(classify: number, width: number, height: number): string | undefined {
    const [minimumWidth, minimumHeight] = getAdPageImageDimensions(classify);
    return width < minimumWidth || height < minimumHeight ? AD_PAGE_IMAGE_DIMENSION_ERROR : undefined;
}

/** 读取本地图片尺寸，复用 Vue 上传组件的浏览器图片解码行为。 */
function readAdPageImageDimensions(file: File): Promise<[number, number]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
            const image = new Image();
            image.onload = () => resolve([image.width, image.height]);
            image.onerror = () => reject(new Error("图片尺寸读取失败"));
            image.src = String(event.target?.result || "");
        };
        reader.onerror = () => reject(new Error("图片文件读取失败"));
        reader.readAsDataURL(file);
    });
}

/** 按 Vue 按钮类型规则返回按钮值校验文案。 */
export function getAdPageButtonValueError(buttonType?: number, value?: string): string | undefined {
    if (buttonType === 4) {
        if (!value?.trim()) return "请输入联系方式";
        if (!/^1[3-9]\d{9}$/.test(value.trim())) return "请输入正确的手机号";
        return undefined;
    }
    if (buttonType === 1 && !value?.trim()) return "请选择商品";
    if (buttonType === 2 && !value?.trim()) return "请选择砂石指数";
    if (buttonType === 3 && !value?.trim()) return "请选择航线";
    if (buttonType === 5 && !value?.trim()) return "请选择资讯";
    return undefined;
}

/** 切换为不需要按钮时清理 Vue 表单中已隐藏的按钮字段。 */
export function resetAdPageButtonValues(values: AdPageFormValues, isNeedButton: number): AdPageFormValues {
    if (isNeedButton) return values;
    return { ...values, buttonType: undefined, buttonValue: undefined, buttonName: undefined };
}

/** 广告新增/编辑表单，保留 Vue 图片/文本和按钮配置分支。 */
export function AdPageFormPage({
    id,
    loadDetail = loadAdPageFormDetail,
    loadProductOptions = loadAdPageProductOptions,
    loadProductTypeOptions = loadAdPageProductTypeOptions,
    loadRouteOptions = loadAdPageRouteOptions,
    loadInformationOptions = loadAdPageInformationOptions,
    create = createAdPage,
    update = updateAdPage
}: AdPageFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const routeId = id ?? Number(searchParams.get("id"));
    const [values, setValues] = useState<AdPageFormValues>(initialValues);
    const [errors, setErrors] = useState<string[]>([]);
    const [productOptions, setProductOptions] = useState<AdPageSelectorOption[]>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<AdPageSelectorOption[]>([]);
    const [routeOptions, setRouteOptions] = useState<AdPageSelectorOption[]>([]);
    const [informationOptions, setInformationOptions] = useState<AdPageSelectorOption[]>([]);

    /** 按 Vue LimitAdImage 契约校验格式、大小及终端最低分辨率。 */
    const beforeUpload = async (file: File) => {
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!AD_PAGE_IMAGE_TYPES.has(file.type) || !["jpg", "jpeg", "png"].includes(extension || "")) {
            antdMessage.error("广告图片仅支持 JPEG、PNG 格式");
            return false;
        }
        if (file.size > AD_PAGE_IMAGE_MAX_SIZE) {
            antdMessage.error("广告图片大小不能超过5MB");
            return false;
        }
        try {
            const [width, height] = await readAdPageImageDimensions(file);
            const dimensionError = getAdPageImageDimensionError(values.classify, width, height);
            if (dimensionError) {
                antdMessage.error(dimensionError);
                return false;
            }
            return true;
        } catch {
            antdMessage.error("广告图片分辨率校验失败");
            return false;
        }
    };

    /** 复用 Vue 文件服务完成广告图片上传，并将文件 ID 回填表单。 */
    const uploadImage = async (options: UploadRequestOptions) => {
        try {
            const formData = new FormData();
            formData.append("file", options.file);
            formData.append("fileName", options.file.name);
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const pictureId = Number(uploadedFile.id);
            if (!Number.isInteger(pictureId) || pictureId <= 0) throw new Error("invalid file id");
            setValues(current => ({ ...current, pictureId }));
            options.onSuccess(uploadedFile);
        } catch {
            antdMessage.error("广告图片上传失败");
            options.onError(new Error("广告图片上传失败"));
        }
    };

    /** 按 Vue remote-select 行为查询按钮关联候选项。 */
    const searchButtonOptions = async (
        keyword: string,
        loader: (keyword: string) => Promise<AdPageSelectorOption[]>,
        setOptions: (options: AdPageSelectorOption[]) => void,
        failureMessage: string
    ) => {
        if (!keyword.trim()) {
            setOptions([]);
            return;
        }
        try {
            setOptions((await loader(keyword)) || []);
        } catch {
            setOptions([]);
            antdMessage.error(failureMessage);
        }
    };

    useEffect(() => {
        if (!Number.isFinite(routeId) || routeId <= 0) return;
        let disposed = false;
        void loadDetail(routeId)
            .then(result => {
                if (disposed) return;
                const advert = result.advert;
                setValues({
                    title: advert.title || "",
                    classify: Number(advert.classify) || 1,
                    type: Number(advert.type) || 1,
                    pictureId: advert.pictureId,
                    // Vue WangEditor 编辑态移除媒体 ID 头部，仅向编辑器回填真实正文。
                    content: ((advert as typeof advert & { content?: string }).content || "").replace(
                        EDITOR_FILE_HEADER,
                        ""
                    ),
                    isNeedButton: Number(advert.isNeedButton) || 0,
                    buttonType: advert.buttonType === undefined ? undefined : Number(advert.buttonType),
                    buttonValue: advert.buttonValue,
                    buttonName: advert.buttonName
                });

                // Vue 编辑态先把已有按钮值加入候选，确保远程选择器能回显可读名称。
                const buttonType = Number(advert.buttonType);
                const remoteLoaders: Record<
                    number,
                    ((keyword: string) => Promise<AdPageSelectorOption[]>) | undefined
                > = {
                    1: loadProductOptions,
                    2: loadProductTypeOptions,
                    3: loadRouteOptions,
                    5: loadInformationOptions
                };
                const remoteSetters: Record<number, ((options: AdPageSelectorOption[]) => void) | undefined> = {
                    1: setProductOptions,
                    2: setProductTypeOptions,
                    3: setRouteOptions,
                    5: setInformationOptions
                };
                const loader = advert.buttonValue ? remoteLoaders[buttonType] : undefined;
                const setter = remoteSetters[buttonType];
                if (loader && setter && advert.buttonValue) {
                    void loader(advert.buttonValue)
                        .then(options => {
                            if (!disposed) setter(options);
                        })
                        .catch(() => {
                            if (!disposed) antdMessage.error("广告按钮值加载失败");
                        });
                }
            })
            .catch(() => {
                if (!disposed) antdMessage.error("广告表单加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, loadInformationOptions, loadProductOptions, loadProductTypeOptions, loadRouteOptions, routeId]);

    const buttonSelectorConfig: Record<
        number,
        {
            label: string;
            searchLabel: string;
            placeholder: string;
            options: AdPageSelectorOption[];
            load: (keyword: string) => Promise<AdPageSelectorOption[]>;
            setOptions: (options: AdPageSelectorOption[]) => void;
            failureMessage: string;
        }
    > = {
        1: {
            label: "商品",
            searchLabel: "商品搜索",
            placeholder: "请搜索商品名称或商品id选择",
            options: productOptions,
            load: loadProductOptions,
            setOptions: setProductOptions,
            failureMessage: "商品搜索失败"
        },
        2: {
            label: "砂价",
            searchLabel: "砂价搜索",
            placeholder: "请搜索品类名称或品类代码选择",
            options: productTypeOptions,
            load: loadProductTypeOptions,
            setOptions: setProductTypeOptions,
            failureMessage: "砂价搜索失败"
        },
        3: {
            label: "运价",
            searchLabel: "运价搜索",
            placeholder: "请搜索码头名称或航线代码选择",
            options: routeOptions,
            load: loadRouteOptions,
            setOptions: setRouteOptions,
            failureMessage: "运价搜索失败"
        },
        5: {
            label: "资讯",
            searchLabel: "资讯搜索",
            placeholder: "请搜索资讯标题",
            options: informationOptions,
            load: loadInformationOptions,
            setOptions: setInformationOptions,
            failureMessage: "资讯搜索失败"
        }
    };

    /** 按 Vue 按钮类型渲染联系方式输入或关联实体远程选择器。 */
    const renderButtonValue = () => {
        const buttonType = Number(values.buttonType);
        if (buttonType === 4) {
            return (
                <Input
                    aria-label="联系方式"
                    placeholder="请输入联系方式"
                    value={values.buttonValue || ""}
                    onChange={event => setValues(current => ({ ...current, buttonValue: event.target.value }))}
                />
            );
        }
        const config = buttonSelectorConfig[buttonType];
        if (!config) return null;
        return (
            <Select
                allowClear
                aria-label={config.label}
                filterOption={false}
                options={config.options.map(option => ({ label: option.label, value: String(option.id) }))}
                placeholder={config.placeholder}
                showSearch
                value={values.buttonValue || undefined}
                onChange={buttonValue => setValues(current => ({ ...current, buttonValue }))}
                onSearch={keyword =>
                    void searchButtonOptions(keyword, config.load, config.setOptions, config.failureMessage)
                }
            />
        );
    };

    /** 切换按钮类型时清理旧类型的候选项和已隐藏的按钮值。 */
    const handleButtonTypeChange = (buttonType: number) => {
        setProductOptions([]);
        setProductTypeOptions([]);
        setRouteOptions([]);
        setInformationOptions([]);
        setValues(current => ({ ...current, buttonType, buttonValue: "" }));
    };

    const submit = async () => {
        const nextErrors: string[] = [];
        if (!values.title.trim()) nextErrors.push("请输入标题");
        if (values.type === 1 && (!values.pictureId || values.pictureId <= 0)) nextErrors.push("请上传广告图片");
        if (values.type === 2 && !values.content?.trim()) nextErrors.push("请输入内容");
        if (values.isNeedButton === 1) {
            if (!values.buttonType) nextErrors.push("请选择按钮类型");
            else {
                const buttonValueError = getAdPageButtonValueError(values.buttonType, values.buttonValue);
                if (buttonValueError) nextErrors.push(buttonValueError);
            }
            if (!values.buttonName?.trim()) nextErrors.push("请输入按钮名称");
        }
        setErrors(nextErrors);
        if (nextErrors.length) return;

        const payload: AdPageFormValues = {
            title: values.title.trim(),
            classify: values.classify,
            type: values.type,
            isNeedButton: values.isNeedButton
        };
        if (values.type === 1) payload.pictureId = values.pictureId;
        else {
            // Vue 提交前根据正文媒体地址重新挂载文件 ID，保持广告接口的富文本契约。
            const editorContent = values.content || "";
            payload.content = setAdPageEditorContent(editorContent, getAdPageActiveFileIds(editorContent));
        }
        if (values.isNeedButton === 1) {
            payload.buttonType = values.buttonType;
            payload.buttonValue = values.buttonValue;
            payload.buttonName = values.buttonName;
        }
        try {
            if (Number.isFinite(routeId) && routeId > 0) await update(routeId, payload);
            else await create(payload);
            antdMessage.success("广告保存成功");
            // Vue 保存成功后返回上一历史记录，避免停留在已提交的表单页。
            void navigate(-1);
        } catch {
            antdMessage.error("广告保存失败");
        }
    };

    return (
        <section aria-label="ad-page-form-page" className="flex h-full min-h-full flex-col">
            <Card className="mb-20">
                <Typography.Title level={4}>{routeId > 0 ? "编辑广告页" : "新增广告页"}</Typography.Title>
                {errors.map(error => (
                    <Alert key={error} className="mb-2" message={error} role="alert" type="error" />
                ))}
                {/* 旧 Vue 主表单只占页面左半区，分类与广告类型各占同一行的一半。 */}
                <ZhGrid cols={2} gap={[20, 0]}>
                    <Form labelAlign="left" labelCol={{ flex: "90px" }}>
                        <ZhGrid cols={2} gap={[20, 0]}>
                            <Form.Item label="分类" required>
                                <Radio.Group
                                    aria-label="广告分类"
                                    options={[
                                        { label: "PC", value: 1 },
                                        { label: "移动端", value: 2 }
                                    ]}
                                    value={values.classify}
                                    onChange={event =>
                                        setValues(current => ({ ...current, classify: Number(event.target.value) }))
                                    }
                                />
                            </Form.Item>
                            <Form.Item label="广告类型" required>
                                <Radio.Group
                                    aria-label="广告类型"
                                    options={[
                                        { label: "图片", value: 1 },
                                        { label: "文本", value: 2 }
                                    ]}
                                    value={values.type}
                                    onChange={event =>
                                        setValues(current => ({ ...current, type: Number(event.target.value) }))
                                    }
                                />
                            </Form.Item>
                        </ZhGrid>
                        <Form.Item label="标题" required>
                            <Input
                                allowClear
                                aria-label="广告标题"
                                maxLength={50}
                                placeholder="请输入标题"
                                showCount
                                value={values.title}
                                onChange={event => setValues(current => ({ ...current, title: event.target.value }))}
                            />
                        </Form.Item>
                        {values.type === 1 ? (
                            <Form.Item
                                label="上传图片"
                                labelCol={{ span: 24 }}
                                required
                                extra={`图片尺寸要求: ${getAdPageImageDimensions(values.classify)[0]}px * ${getAdPageImageDimensions(values.classify)[1]}px,仅支持JPEG、PNG格式，单张最大5MB，请勿包含敏感信息或侵权内容`}
                            >
                                <div className="rounded-md bg-[#fafafa] p-5">
                                    <ZhUpload
                                        action=""
                                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                        aria-label="广告图片上传"
                                        beforeUpload={beforeUpload}
                                        limit={1}
                                        httpRequest={uploadImage}
                                        showFileList={false}
                                    >
                                        <Button htmlType="button">{values.pictureId ? "替换图片" : "上传图片"}</Button>
                                    </ZhUpload>
                                </div>
                            </Form.Item>
                        ) : (
                            <Form.Item label="内容" labelCol={{ span: 24 }}>
                                <WangEditor
                                    height="350px"
                                    placeholder="请输入内容..."
                                    value={values.content || ""}
                                    onUpdateValue={content => setValues(current => ({ ...current, content }))}
                                />
                            </Form.Item>
                        )}
                        <Form.Item label="是否需要按钮" labelCol={{ flex: "110px" }} required>
                            <Radio.Group
                                aria-label="是否需要按钮"
                                options={[
                                    { label: "是", value: 1 },
                                    { label: "否", value: 0 }
                                ]}
                                value={values.isNeedButton}
                                onChange={event => {
                                    const isNeedButton = Number(event.target.value);
                                    setValues(current =>
                                        resetAdPageButtonValues({ ...current, isNeedButton }, isNeedButton)
                                    );
                                }}
                            />
                        </Form.Item>
                        {values.isNeedButton === 1 ? (
                            <>
                                <Form.Item label="按钮类型" labelCol={{ flex: "110px" }} required>
                                    <Radio.Group
                                        aria-label="按钮类型"
                                        options={[
                                            { label: "联系方式", value: 4 },
                                            { label: "商品", value: 1 },
                                            { label: "砂价", value: 2 },
                                            { label: "运价", value: 3 },
                                            { label: "资讯", value: 5 }
                                        ]}
                                        value={values.buttonType}
                                        onChange={event => handleButtonTypeChange(Number(event.target.value))}
                                    />
                                </Form.Item>
                                <ZhGrid cols={2} gap={[20, 0]}>
                                    <Form.Item
                                        label={buttonSelectorConfig[Number(values.buttonType)]?.label || "联系方式"}
                                    >
                                        {renderButtonValue()}
                                    </Form.Item>
                                    <Form.Item label="按钮名称" required>
                                        <Input
                                            aria-label="按钮名称"
                                            maxLength={15}
                                            placeholder="请输入按钮名称"
                                            showCount
                                            value={values.buttonName || ""}
                                            onChange={event =>
                                                setValues(current => ({ ...current, buttonName: event.target.value }))
                                            }
                                        />
                                    </Form.Item>
                                </ZhGrid>
                            </>
                        ) : null}
                    </Form>
                </ZhGrid>
            </Card>
            <Space className="operation-fixed-btn" size={0}>
                <Button aria-label="提交广告" type="primary" onClick={() => void submit()}>
                    提交
                </Button>
                <Button aria-label="返回" onClick={() => void navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}

export default AdPageFormPage;
